// #region Imports
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Grow from "@mui/material/Grow";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { useInfiniteScroll } from "_shared/_hooks/infinite-scroll/use-infinite-scroll";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { useImagePreviewDialog } from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import downloadFile from "_shared/_utilities/download-file";
import { CanceledError } from "axios";
import * as Config from "config";
import Individual from "marketplace/individuals/individual";
import { individualsApi } from "marketplace/individuals/_api/individual-api";
import MessagingAPIService, { AttachmentQuery, MessageQuery } from "messaging/_api/messaging-api-service";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import {
  getIsLoadingMessagesByForumId,
  getMessagesByForumId,
  populateForumMessages,
} from "messaging/_stores/messages-redux-slice";
import MessageEditor from "messaging/_views/components/editor/message-editor";
import SearchBar from "messaging/_views/components/message-search-bar";
import { ReceivedMessage } from "messaging/_views/components/received-message-container";
import { SentMessage } from "messaging/_views/components/sent-message-container";
import AttachmentParameters, {
  AttachmentFilterParameters,
} from "messaging/attachments/_api/queries/attachment-parameters";
import {
  getAttachmentsByForumId,
  getIsLoadingAttachmentsByForumId,
  populateForumAttachments,
} from "messaging/attachments/_stores/message-attachments-redux-slice";
import AttachmentListItem from "messaging/attachments/_views/message-attachment-list-item";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import TimelineOverlay from "messaging/forums/_views/components/forum-timeline-overlay";
import Forum from "messaging/forums/forum";
import SubscribersComponent from "messaging/forums/subscribers/_views/subscriber-list";
import AddSubscriber from "messaging/forums/subscribers/invitations/_views/invite-forum-subscriber-view";
import Message from "messaging/message";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSession } from "users/__session/session-context";
// #endregion

// #region Styled Components
const ForumLoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));
const MessageLoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.primary.main,
  height: "32px",
  margin: theme.spacing(2),
  width: "32px",
}));
const TopLoadingIndicatorContainer = styled("div")(() => ({
  backgroundColor: "#f8f9fd",
  display: "flex",
  justifyContent: "center",
  paddingBottom: "8px",
  position: "sticky",
  top: "48px",
  width: "100%",
}));
const OuterContainer = styled("div")(() => ({
  display: "grid",
  gridTemplateRows: "auto 1fr auto",
  height: "75vh",
  maxHeight: "75vh",
}));
const ForumTabs = styled(Tabs)(() => ({
  gridRowStart: 1,
  "& .MuiTabs-flexContainer": {
    borderBottom: `1px solid #aaa`,
    height: "100%",
  },
}));
const ForumTab = styled(Tab)(() => ({
  backgroundColor: "#f8f9fd",
  borderTopLeftRadius: "4px",
  borderTopRightRadius: "4px",
  flex: 1,
  "&.Mui-selected": {
    border: `1px solid #aaa`,
    bottom: "-1px",
    borderBottom: "none",
  },
}));
const TabLabelContainer = styled("div")(() => ({
  fontSize: "0.85em",
  justifyItems: "space-between",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "100%",
}));
const ForumTabLabel = styled("span")(() => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
}));
const CloseButton = styled(CloseIcon)(({ theme }) => ({
  color: theme.palette.text.secondary,
  cursor: "pointer",
  marginLeft: theme.spacing(1),
  "&:hover": {
    color: theme.palette.primary.dark,
  },
}));
const MessagePane = styled("div")(() => ({
  containerType: "inline-size",
  overflowY: "scroll",
  position: "relative",
  scrollbarGutter: "stable",
  width: "100%",
}));
const MessageContainer = styled("div")(({ theme }) => ({
  backgroundColor: "#f8f9fd",
  border: "1px solid #aaa",
  borderTop: "none",
  display: "inline-grid",
  gridRowStart: 2,
  gridTemplateRows: "1fr auto",
  minHeight: 0,
  paddingLeft: theme.spacing(2),
  position: "relative",
  width: "100%",
}));
const EmptyMessage = styled(Typography)(() => ({
  alignContent: "center",
  alignItems: "center",
  color: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  fontSize: "1.33em",
  height: "100%",
  justifyContent: "center",
  textAlign: "center",
  whiteSpace: "normal",
  width: "100%",
}));
const BackToTopChipContainer = styled("div")(() => ({
  display: "flex",
  height: 0,
  justifyContent: "center",
  position: "relative",
  width: "100%",
  zIndex: 1,
}));
const BackToTopChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  bottom: "20px",
  boxShadow: "0 4px 5px 0 rgb(0 0 0 / 14%), 0 1px 10px 0 rgb(0 0 0 / 12%), 0 2px 4px 0 rgb(0 0 0 / 20%)",
  color: "white",
  position: "absolute",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
  },
}));
const EditorContainer = styled("div")(() => ({
  gridRowStart: 3,
  width: "100%",
}));
const SearchField = styled(SearchBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  "&:before": {
    backgroundColor: "#f8f9fd",
    content: "''",
    height: "8px",
  },
}));
// #endregion

export type UserAvatarMap = {
  [userId: string]: string;
};

type ChatWindowProps = {
  className?: string;
  forums: Forum[];
  locked?: boolean;
  lockedMessage?: string;
  readOnly?: boolean;
  onClose?: () => void;
  onLeaveConversation?: () => void;
};

type ActiveForumSubscriberData = {
  forumId?: string;
  subscribers: Individual[];
  avatars: UserAvatarMap;
};

export default function ChatWindow(props: Readonly<ChatWindowProps>) {
  const { className, forums, locked, lockedMessage, readOnly, onClose, onLeaveConversation } = props;

  // #region Refs
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagePaneRef = useRef<HTMLDivElement>(null);

  const visibleForums: Forum[] = useMemo(() => {
    return forums?.filter((forum) => (forum?.subscribers?.length ?? 0) > 1) ?? [];
  }, [forums]);

  // #region State
  const [activeForumId, setActiveForumId] = useState<string | undefined>();
  const [activeForumSubscriberData, setActiveForumSubscriberData] = useState<ActiveForumSubscriberData>({
    subscribers: [],
    avatars: {},
  });
  const [searchString, setSearchString] = useState<string | undefined>();
  const [minCreatedDate, setMinCreatedDate] = useState<string | undefined>();
  const [maxCreatedDate, setMaxCreatedDate] = useState<string | undefined>();
  const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
  const [isLoadingForum, setIsLoadingForum] = useState(false);
  const [backToTopVisible, setBackToTopVisible] = useState(false);
  const [showSystemMessages, setShowSystemMessages] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [filterDates, setFilterDates] = useState(false);
  const [timelineActive, setTimelineActive] = useState(false);
  const activeForum = useMemo(() => {
    return visibleForums.find((forum) => forum.id?.value === activeForumId) ?? visibleForums[0];
  }, [activeForumId, visibleForums]);
  const activeForumSubscribers =
    activeForumSubscriberData.forumId === activeForum?.id?.value ? activeForumSubscriberData.subscribers : [];
  const activeForumAvatars =
    activeForumSubscriberData.forumId === activeForum?.id?.value ? activeForumSubscriberData.avatars : {};

  // #region Selectors
  const attachments = useSelector(getAttachmentsByForumId(activeForum?.id));
  const isLoadingAttachments = useSelector(getIsLoadingAttachmentsByForumId(activeForum?.id));
  const messages = useSelector(getMessagesByForumId(activeForum?.id));
  const isLoadingMessages = useSelector(getIsLoadingMessagesByForumId(activeForum?.id));
  const latestMessageId = messages.data?.at(-1)?.id?.value;

  // #region Hooks
  const { openDialog, closeDialog } = useDialog();
  const { openImagePreviewDialog } = useImagePreviewDialog();
  const session = useSession();
  const dispatch = useAttorneyHubDispatch();
  useInfiniteScroll(
    // Hook for infinite message loading
    fetchMoreMessages,
    messages.hasNextPage,
    isLoadingMessages,
    messagePaneRef,
    true
  );
  useInfiniteScroll(
    // Hook for infinite attachment loading
    fetchMoreAttachments,
    attachments.hasNextPage,
    isLoadingForum || isLoadingAttachments,
    messagePaneRef,
    true
  );

  const fetchUserAvatars = useCallback(
    async (
      subscribers: Individual[]
    ): Promise<UserAvatarMap> => {
      let userAvatars: UserAvatarMap = {};
      for (let subscriber of subscribers) {
        if (subscriber.profile?.avatarId && !userAvatars[subscriber.id.value]) {
          try {
            const avatarData = await dispatch(
              individualsApi.endpoints.getIndividualAvatar.initiate({ individualId: subscriber.id.value }),
            ).unwrap();
            if (avatarData) {
              userAvatars[subscriber.id.value] = avatarData;
            }
          } catch (err) {
            console.warn(err);
          }
        }
      }
      return userAvatars;
    },
    [dispatch]
  );

  const loadSubscribers = useCallback(
    async (forum: Forum, abortController: AbortController) => {
      const fetchSubscribers = async (abortController: AbortController): Promise<Individual[]> => {
        return await new MessagingAPIService(session).getForumSubscriberInfo(forum, abortController);
      };

      try {
        const subscribers = await fetchSubscribers(abortController);
        const userAvatars = await fetchUserAvatars(subscribers);

        setActiveForumSubscriberData({
          forumId: forum.id?.value,
          subscribers,
          avatars: userAvatars,
        });
      } catch (err) {
        if (err instanceof CanceledError) return;
        console.error(err);
        enqueueSnackbar("Failed to get subscribers", { variant: "error" });
      }
    },
    [session, fetchUserAvatars]
  );


  const handleWindowScrolled = useCallback(() => {
    const pane = messagePaneRef.current;
    if (!pane) return;

    const { scrollTop, scrollHeight, clientHeight } = pane;
    const maxScroll = scrollHeight - clientHeight;

    // Hide the back to top button if there's nothing to scroll
    if (maxScroll <= 0) {
      setBackToTopVisible(false);
      return;
    }

    // Otherwise, show the button if we’re at the bottom
    const scrollPercent = Math.round((scrollTop / maxScroll) * 100);
    setBackToTopVisible(scrollPercent < 99);

    // If we are at the bottom, set initialLoadCompleted to true
    if (!initialLoadCompleted && scrollPercent === 100) {
      setInitialLoadCompleted(true);
    }
  }, [initialLoadCompleted]);

  const scrollToBottom = useCallback(
    (delayMs: number = 0) => {
      if (!bottomRef.current) return;
      setTimeout(() => {
        bottomRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }, delayMs);
    },
    [bottomRef]
  );

  const loadActiveForumSubscribersAndAvatars = useCallback(
    async (forum: Forum, abortController: AbortController) => {
      setIsLoadingForum(true);
      try {
        await loadSubscribers(forum, abortController);
      } finally {
        setIsLoadingForum(false);
      }
    },
    [loadSubscribers]
  );

  const populateAttachments = useCallback(() => {
    if (!activeForum) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    dispatch(
      populateForumAttachments({
        session,
        forum: activeForum,
        query,
        append: !searchString && !showSystemMessages && !filterDates,
      })
    );
  }, [activeForum, dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, session, showSystemMessages]);

  const populateMessages = useCallback(() => {
    if (!activeForum) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
        })
      ),
      PaginationParameters.create({
        pageIndex: 0,
        pageSize: Config.chatPageSize,
      })
    );
    dispatch(
      populateForumMessages({
        session,
        forum: activeForum,
        query,
        append: !searchString && !showSystemMessages && !filterDates,
      })
    );
  }, [activeForum, dispatch, filterDates, maxCreatedDate, minCreatedDate, searchString, session, showSystemMessages]);

  // #region Lifecycle
  // 1. DOM Event Listeners (Runs once on mount)
  useEffect(() => {
    const pane = messagePaneRef.current;

    const handlePointerDown = (e: PointerEvent) => {
      if (!pane) return;
      const { right } = pane.getBoundingClientRect();
      const scrollbarWidth = 16;
      if (e.clientX >= right - scrollbarWidth && e.clientX <= right) {
        setTimelineActive(true);
      }
    };

    const timeoutId = globalThis.setTimeout(() => {
      pane?.addEventListener("scroll", handleWindowScrolled, { passive: true });
      pane?.addEventListener("pointerdown", handlePointerDown as EventListener, true);
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      pane?.removeEventListener("scroll", handleWindowScrolled);
      pane?.removeEventListener("pointerdown", handlePointerDown as EventListener, true);
    };
  }, [handleWindowScrolled]);

  // 2. Load subscribers for the active forum
  useEffect(() => {
    if (!activeForum) return

    const abortController = new AbortController();
    let active = true;
    queueMicrotask(() => {
      if (!active) return;
      void loadActiveForumSubscribersAndAvatars(activeForum, abortController);
    });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [activeForum, loadActiveForumSubscribersAndAvatars]);

  // 4. Scroll to bottom on message load
  useEffect(() => {
    if (isLoadingMessages) return;
    scrollToBottom();
  }, [isLoadingMessages, scrollToBottom, showAttachments, showSystemMessages]);

  useEffect(() => {
    if (showAttachments || !latestMessageId) return;
    scrollToBottom();
  }, [latestMessageId, scrollToBottom, showAttachments]);

  useEffect(() => {
    if (!activeForum) return;

    let abortController = new AbortController();

    if (showAttachments) {
      populateAttachments();
    } else {
      populateMessages();
    }

    return () => {
      abortController.abort();
      abortController = new AbortController();
    };
  }, [activeForum, populateAttachments, populateMessages, showAttachments]);
  // #endregion

  // #region Functions
  function fetchMoreMessages() {
    if (!activeForum || !messages.hasNextPage || isLoadingForum || !initialLoadCompleted) return;

    const query = new MessageQuery(
      new MessageParameters(
        new MessageFilterParameters({
          searchString,
          systemOnly: showSystemMessages,
          minCreatedDate,
          maxCreatedDate,
        })
      ),
      PaginationParameters.create({
        pageIndex: messages.pageIndex + 1,
        pageSize: messages.pageSize,
      })
    );
    dispatch(
      populateForumMessages({
        session,
        forum: activeForum,
        query,
        append: true,
      })
    );

    const pane = messagePaneRef.current;
    if (!pane) return;

    const prevHeight = pane.scrollHeight;

    // Prevent the infinite loading from triggering more than once
    requestAnimationFrame(() => {
      const newHeight = pane.scrollHeight;
      pane.scrollTop = newHeight - prevHeight;
    });
  }

  function fetchMoreAttachments() {
    if (!activeForum || !attachments.hasNextPage || isLoadingForum || !initialLoadCompleted) return;

    const query = new AttachmentQuery(
      new AttachmentParameters(new AttachmentFilterParameters(searchString, minCreatedDate, maxCreatedDate)),
      PaginationParameters.create({
        pageIndex: attachments.pageIndex + 1,
        pageSize: attachments.pageSize,
      })
    );
    dispatch(
      populateForumAttachments({
        session,
        forum: activeForum,
        query,
        append: true,
      })
    );

    const pane = messagePaneRef.current;
    if (!pane) return;

    const prevHeight = pane.scrollHeight;

    // Prevent the infinite loading from triggering more than once
    requestAnimationFrame(() => {
      const newHeight = pane.scrollHeight;
      pane.scrollTop = newHeight - prevHeight;
    });
  }

  function handleTimelineDateSelected(startDate?: string, endDate?: string) {
    if (!startDate || !endDate) {
      setTimelineActive(false);
      return;
    }
    setFilterDates(true);
    setMinCreatedDate(startDate);
    setMaxCreatedDate(endDate);
  }

  function handleTimelineReset() {
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setFilterDates(false);
  }

  function handleAddSubscriberPressed(): void {
    if (!activeForum) return;
    const component = <AddSubscriber forum={activeForum} onClosed={closeDialog} />;
    openDialog({ component: component, title: "Add People to Chat" });
  }

  function handleLeaveConversationPressed(): void {
    onLeaveConversation?.();
    closeDialog();
  }

  function handleToggleAttachments() {
    if (!showAttachments) {
      populateAttachments();
    }
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setShowAttachments(!showAttachments);
  }

  function handleToggleSystemMessages() {
    setInitialLoadCompleted(false);
    setSearchString(undefined);
    setMinCreatedDate(undefined);
    setMaxCreatedDate(undefined);
    setShowSystemMessages(!showSystemMessages);
  }

  function handleToggleFilterDates(): void {
    if (filterDates) {
      setMinCreatedDate(undefined);
      setMaxCreatedDate(undefined);
      setTimelineActive(false);
      setFilterDates(false);
    } else {
      setTimelineActive(true);
    }
  }

  function handleSearch(searchString: string): void {
    setSearchString(searchString);
    if (searchString === "") {
      scrollToBottom(1000);
    }
  }

  function getEmptyMessageString(): string {
    if (locked) {
      return lockedMessage ?? "Chat was locked by the moderator";
    }
    if (searchString) {
      return "No Matching Results";
    }
    return "No Messages";
  }

  /**
   * Get the chat title based on the forum name after removing the current user
   * @param title The forum name
   * @returns The chat title
   */
  function getChatTitle(title: string | undefined): string {
    if (!title) return "Messages";
    const participants = title.split(",").map((p) => p.trim());
    const filtered = participants.filter((participant) => session.user?.fullName !== participant);
    return filtered.join(", ");
  }

  function openImagePreview(message: Message, image: MessageFileInfo) {
    if (!image.dataUri) {
      enqueueSnackbar("Image not found", { variant: "error" });
      console.error("Image not found");
      return;
    }
    onClose?.();
    openImagePreviewDialog(
      {
        name: image.name,
        src: image.dataUri,
      },
      () => downloadAttachment(message, image)
    );
  }

  async function downloadAttachment(message: Message, attachment: MessageFileInfo): Promise<void> {
    try {
      if (!message.forum.id) {
        throw new Error("Forum ID is undefined");
      }
      const messagingService = new MessagingAPIService(session);
      const response = await messagingService.getMessageAttachmentById(message.forum.id, attachment.id);
      await downloadFile(response, attachment.name);
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Failed to get message attachment. Please try again.", { variant: "error" });
    }
  }
  // #endregion

  // #region Render
  return (
    <OuterContainer className={className}>
      <ForumTabs
        value={activeForum?.id?.value ?? false}
        onChange={(_, newValue) => {
          if (visibleForums.some((forum) => forum.id?.value === newValue)) {
            setActiveForumId(newValue);
          }
        }}
        slotProps={{
          indicator: {
            style: {
              backgroundColor: "transparent",
            },
          },
        }}
        textColor="primary"
      >
        {visibleForums.length === 0 && <ForumTab disabled label="No Forums Found" value="no-forums" />}
        {visibleForums.map(
          (forum: Forum) =>
            (forum?.subscribers?.length ?? 0) > 1 && (
              <ForumTab
                key={forum.id?.value}
                disabled={locked || readOnly}
                value={forum.id?.value}
                onClick={() => setActiveForumId(forum.id?.value)}
                style={{ maxWidth: `calc((100%/${forums.length}) - 48px)` }}
                label={
                  <TabLabelContainer>
                    {onClose && (
                      <ForumTabLabel>
                        {getChatTitle(forum.name.toString())}
                        <Tooltip title="Close Chat">
                          <CloseButton
                            onClick={(e) => {
                              e.stopPropagation();
                              onClose();
                            }}
                          />
                        </Tooltip>
                      </ForumTabLabel>
                    )}
                    {!onClose && getChatTitle(forum.name.toString())}
                  </TabLabelContainer>
                }
              />
            )
        )}
        <SubscribersComponent
          subscribers={activeForumSubscribers}
          forum={activeForum}
          avatars={activeForumAvatars}
          disableLeaveButton={locked || readOnly}
          onAddSubscriber={handleAddSubscriberPressed}
          onLeaveConversation={handleLeaveConversationPressed}
        />
      </ForumTabs>
      <MessageContainer>
        <MessagePane ref={messagePaneRef}>
          <SearchField
            showSystemMessages={showSystemMessages}
            showAttachments={showAttachments}
            filterDates={filterDates}
            onToggleDates={handleToggleFilterDates}
            onToggleSystemMessages={handleToggleSystemMessages}
            onToggleAttachments={handleToggleAttachments}
            onSearch={handleSearch}
          />
          {(isLoadingMessages || isLoadingAttachments) && (
            <TopLoadingIndicatorContainer>
              <MessageLoadingIndicator disableShrink thickness={4} />
            </TopLoadingIndicatorContainer>
          )}
          {isLoadingForum && (
            <EmptyMessage>
              <ForumLoadingIndicator disableShrink size={25} /> Loading...
            </EmptyMessage>
          )}
          {!isLoadingForum && (
            <>
              {showAttachments && attachments.isEmpty && !isLoadingAttachments && (
                <EmptyMessage>No Attachments</EmptyMessage>
              )}
              {showAttachments && !attachments.isEmpty && (
                <>
                  {attachments.data?.map((attachment: MessageFileInfo) => (
                    <AttachmentListItem
                      key={attachment.id?.value}
                      session={session}
                      forumId={activeForum?.id}
                      attachmentInfo={attachment}
                    />
                  ))}
                </>
              )}
              {!showAttachments && (
                <>
                  {messages.isEmpty && !isLoadingMessages && <EmptyMessage>{getEmptyMessageString()}</EmptyMessage>}
                  {!locked &&
                    messages.data?.map((message: Message) => {
                      if (session.user && message.userIsSender(session.user)) {
                        return (
                          <SentMessage
                            key={message.id?.value}
                            data-date={message.created?.format("MM/DD/YY hh:mm A")}
                            session={session}
                            message={message}
                            onImageLoad={() => scrollToBottom(1000)}
                            onImageClick={openImagePreview}
                            onDownload={downloadAttachment}
                          />
                        );
                      } else
                        return (
                          <ReceivedMessage
                            key={message.id?.value}
                            data-date={message.created?.format("MM/DD/YY hh:mm A")}
                            session={session}
                            sender={activeForumSubscribers.find((subscriber: Individual) => {
                              return subscriber.userId?.isEqualTo(message.senderId);
                            })}
                            avatars={activeForumAvatars}
                            message={message}
                            onImageLoad={() => scrollToBottom(1000)}
                            onImageClick={openImagePreview}
                            onDownload={downloadAttachment}
                          />
                        );
                    })}
                  {locked && <EmptyMessage>{lockedMessage ?? "Chat was locked by the moderator"}</EmptyMessage>}
                </>
              )}
            </>
          )}
          <div ref={bottomRef} />
        </MessagePane>
        <TimelineOverlay
          show={timelineActive}
          firstDate={showAttachments ? attachments.minCreatedDate : messages.minCreatedDate}
          lastDate={showAttachments ? attachments.maxCreatedDate : messages.maxCreatedDate}
          onDateSelected={handleTimelineDateSelected}
          onReset={handleTimelineReset}
        />
        {((!isLoadingForum && showAttachments && !attachments.isEmpty) || (!showAttachments && !messages.isEmpty)) && (
          <BackToTopChipContainer>
            <Grow in={backToTopVisible}>
              <BackToTopChip
                label="Jump to bottom"
                icon={<ArrowDownwardIcon style={{ color: "white" }} />}
                onClick={() => scrollToBottom()}
              />
            </Grow>
          </BackToTopChipContainer>
        )}
      </MessageContainer>
      {!readOnly && (
        <EditorContainer>
          <MessageEditor
            forum={activeForum}
            onFormattingToggled={() => scrollToBottom(1000)}
            onAttachmentAdded={() => scrollToBottom(1000)}
            onMessageSent={() => scrollToBottom(1000)}
            locked={locked}
          />
        </EditorContainer>
      )}
    </OuterContainer>
  );
  // #endregion
}
